import moment, { Moment } from 'moment/moment'

export interface ClubSectionElementPayload {
    id?: number
    element_type: string
    element_id?: number
    sort_order?: number
    is_premium?: boolean
    element?: any
}

export interface ClubSectionPayload {
    id?: number
    name: string
    sort_order?: number
    elements?: ClubSectionElementPayload[]
    translations?: {}
}

export interface ClubPayload {
    id: number
    name: string
    description: string
    photo_url: string
    photos: string[]
    metadata?: {}
    country: string
    sort_order: number
    followers_count: number
    is_follower?: boolean
    enabled?: boolean
    created_at: string
    sections?: ClubSectionPayload[]
    translations?: {}
}

/**
 * A single element placement within a section — dispatches to whichever
 * entity type (training_program / challenge / event / athlete / product /
 * content) is referenced. `itemId` is the target entity's own id, not the
 * pivot row's id (kept separately as `id` only for :key/debugging).
 */
export class ClubSectionElement {

    id?: number
    type: string
    itemId?: number
    name: string
    isPremium: boolean

    constructor(data: ClubSectionElementPayload = <any>{}) {
        this.id = data.id
        this.type = data.element_type
        this.itemId = data.element_id || data.element?.id
        this.name = data.element ? (data.element.name || '') : ''
        this.isPremium = !! data.is_premium
    }

    payload(): any {
        return {
            type: this.type,
            id: this.itemId,
            is_premium: this.isPremium,
        }
    }
}

export class ClubSection {

    id?: number
    name: string
    translations?: {} = <any>{}
    elements: ClubSectionElement[] = []

    // UI-only draft state for the "add element" row — not sent in payload().
    newElementType: string = ''
    newElementId?: number = undefined
    newElementPremium: boolean = false

    constructor(data: ClubSectionPayload = <any>{}) {
        this.id = data.id
        this.name = data.name
        this.translations = data.translations || {}
        this.elements = (data.elements || []).map((item) => new ClubSectionElement(item))
    }

    payload(): any {
        return {
            id: this.id,
            name: this.name,
            translations: this.translations,
            elements: this.elements.map((item) => item.payload()),
        }
    }

    addElement(type: string, itemId: number, name: string, isPremium: boolean = false): this {
        const element = new ClubSectionElement()
        element.type = type
        element.itemId = itemId
        element.name = name
        element.isPremium = isPremium
        this.elements.push(element)
        return this
    }

    removeElement(index: number): this {
        this.elements.splice(index, 1)
        return this
    }

    moveElement(index: number, direction: -1 | 1): this {
        const target = index + direction
        if (target < 0 || target >= this.elements.length) {
            return this
        }
        const [item] = this.elements.splice(index, 1)
        this.elements.splice(target, 0, item)
        return this
    }
}

export class Club {

    id: number
    name: string
    description: string
    photoUrl: string
    photo?: File
    photos: string[] = []
    photoUps?: File[]
    metadata?: {} = <any>{}
    country: string
    sortOrder: number
    followersCount: number
    isFollower: boolean
    enabled: boolean
    createdAt: Moment
    sections: ClubSection[] = []
    translations?: {} = <any>{}

    constructor(data: ClubPayload) {
        this.id = data.id
        this.name = data.name
        this.description = data.description
        this.photoUrl = data.photo_url
        this.photos = data.photos || []
        this.country = data.country
        this.sortOrder = data.sort_order
        this.followersCount = data.followers_count
        this.isFollower = !! data.is_follower
        this.enabled = !! data.enabled
        this.createdAt = moment(data.created_at)
        this.capture(data)
    }

    fill(data: ClubPayload): this {
        this.id = data.id
        this.name = data.name
        this.description = data.description
        this.photoUrl = data.photo_url
        this.photos = data.photos || []
        this.country = data.country
        this.sortOrder = data.sort_order
        this.followersCount = data.followers_count
        this.isFollower = !! data.is_follower
        this.enabled = !! data.enabled
        this.createdAt = moment(data.created_at)
        this.capture(data)
        return this
    }

    capture(data: ClubPayload): this {
        if (data.metadata) {
            this.metadata = data.metadata
        }
        if (data.sections) {
            this.sections = data.sections.map((item) => new ClubSection(item))
        }
        if (data.translations) {
            this.translations = data.translations
        }
        return this
    }

    delPhoto(number: number): this {
        const photos = this.photos.map(i => JSON.parse(JSON.stringify(i)))
        photos.splice(number, 1)
        this.photos = []
        photos.map(i => this.photos.push(i))
        return this
    }

    updatePhotoNumber(currentNumber: number, number: number): this {
        const set = JSON.parse(JSON.stringify(this.photos[currentNumber]))
        this.photos.splice(currentNumber, 1)
        const photos = this.photos.map(i => JSON.parse(JSON.stringify(i)))
        photos.splice(number, 0, set)
        this.photos = []
        photos.map(i => this.photos.push(i))
        return this
    }

    addSection(): this {
        this.sections.push(new ClubSection())
        return this
    }

    removeSection(index: number): this {
        this.sections.splice(index, 1)
        return this
    }

    moveSection(index: number, direction: -1 | 1): this {
        const target = index + direction
        if (target < 0 || target >= this.sections.length) {
            return this
        }
        const [item] = this.sections.splice(index, 1)
        this.sections.splice(target, 0, item)
        return this
    }

    exists(): boolean {
        return !! this.id
    }

    unset(): void {
        this.fill(<any>{})
        this.photo = undefined
        this.photoUps = undefined
        this.photos = []
        this.metadata = {}
        this.sections = []
        this.translations = {}
    }

    payload(): ClubPayload | any {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            photos: this.photos,
            metadata: this.metadata,
            country: this.country,
            sort_order: this.sortOrder,
            enabled: this.enabled,
            sections: this.sections.map((item) => item.payload()),
            translations: this.translations,
        }
    }
}
